import {
  QueryKey,
  useQuery,
  type QueryObserverResult,
  type QueryObserverSuccessResult,
  type UseQueryOptions,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { useOnline } from '@mint-lib/hooks';

// Overload 1: suspense: true (data is always defined)
export function useTypedQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'placeholderData'
  > & {
    suspense: true;
    throwOnError?: boolean;
    placeholderData?: TQueryFnData;
  },
): UseSuspenseQueryResult<TData, TError>;

// Overload 2: placeholderData provided (data is always defined)
export function useTypedQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'placeholderData'
  > & {
    suspense?: false;
    throwOnError?: boolean;
    placeholderData: Exclude<
      UseQueryOptions<
        TQueryFnData,
        TError,
        TData,
        TQueryKey
      >['placeholderData'],
      undefined
    >;
  },
): QueryObserverSuccessResult<TData, TError>;

// Overload 3: no suspense, no placeholderData (data can be undefined)
export function useTypedQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'placeholderData'
  > & {
    suspense?: false;
    throwOnError?: boolean;
    placeholderData?: never;
  },
): QueryObserverResult<TData, TError>;

// Implementation
export function useTypedQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): QueryObserverResult<TData, TError> | UseSuspenseQueryResult<TData, TError> {
  const { throwOnError, ...restOptions } = options;

  const isOnline = useOnline();

  const queryResult = useQuery<TQueryFnData, TError, TData, TQueryKey>({
    throwOnError: (error, query) => {
      if (!throwOnError) {
        return false;
      }

      const notInCache = query.state.data === undefined;

      return Boolean(notInCache && isOnline);
    },
    ...restOptions,
  });

  return {
    ...queryResult,
    // the way placeholderData works is counterintuitive https://github.com/TanStack/query/issues/1712
    isLoading: queryResult.isPlaceholderData || queryResult.isLoading,
    isPending: queryResult.isPlaceholderData || queryResult.isPending,
  } as
    | QueryObserverResult<TData, TError>
    | UseSuspenseQueryResult<TData, TError>;
}
